/**
 * PR13-C (Visual Feedback V2): pure derivation of the status lane's
 * badges (WORMHOLE / SPEED TRIAL / temporary STARFRUIT gate) from the
 * engine's own authoritative remaining-time fields - never a separate
 * UI-owned timer, so Pause freezing the snapshot automatically freezes
 * these too (see `GameScene.update()`'s early return while paused, which
 * simply stops calling anything that would recompute this).
 */

export const WARNING_THRESHOLD_MS = 3000;

export interface StatusSnapshotSlice {
  wormholeRemainingMs: number | null;
  speedTrialRemainingMs: number | null;
  starfruitGateRemainingMs: number | null;
}

export type StatusBadgeId = "wormhole" | "speed-trial" | "starfruit-gate";

export interface StatusBadge {
  id: StatusBadgeId;
  text: string;
  remainingSeconds: number;
  /** True once remaining time is at or under `WARNING_THRESHOLD_MS` - the exact boundary the spec's "last three seconds" rule means. */
  warning: boolean;
}

function secondsCeil(ms: number): number {
  return Math.max(1, Math.ceil(ms / 1000));
}

/** Ordered so the status lane always stacks the same way (never re-sorts as badges appear/disappear) - WORMHOLE, then SPEED TRIAL, then the temporary STARFRUIT gate. */
export function deriveStatusBadges(snapshot: StatusSnapshotSlice): StatusBadge[] {
  const badges: StatusBadge[] = [];
  if (snapshot.wormholeRemainingMs !== null) {
    const seconds = secondsCeil(snapshot.wormholeRemainingMs);
    badges.push({ id: "wormhole", text: `WORMHOLE — ${seconds}s`, remainingSeconds: seconds, warning: snapshot.wormholeRemainingMs <= WARNING_THRESHOLD_MS });
  }
  if (snapshot.speedTrialRemainingMs !== null) {
    const seconds = secondsCeil(snapshot.speedTrialRemainingMs);
    badges.push({ id: "speed-trial", text: `SPEED TRIAL — ${seconds}s`, remainingSeconds: seconds, warning: false });
  }
  if (snapshot.starfruitGateRemainingMs !== null) {
    const seconds = secondsCeil(snapshot.starfruitGateRemainingMs);
    const warning = snapshot.starfruitGateRemainingMs <= WARNING_THRESHOLD_MS;
    badges.push({
      id: "starfruit-gate",
      text: warning ? `GATE CLOSING — ${seconds}s` : `STARFRUIT GATE — ${seconds}s`,
      remainingSeconds: seconds,
      warning,
    });
  }
  return badges;
}
